import React, { useEffect, useState } from "react";
import { Avatar, Tag } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import GetOnHoldTasksApi from "../../api/tasks/GetOnHoldTasksApi";
import PageLoader from "../sub/PageLoader";
import moment from "moment";

export default function DependenciesReport() {
  const [data, setData] = useState({ blocking: [], waiting: [], resolved: [] });
  const [loading, setLoading] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      fetchDependencies();
    }
  }, [perfCycleState, positionState]);

  async function fetchDependencies() {
    setLoading(true);
    const response = await GetOnHoldTasksApi(perfCycleState.id, positionState.id);
    if (response.success) {
      setData(response.data || { blocking: [], waiting: [], resolved: [] });
    }
    setLoading(false);
  }

  if (loading) {
    return <PageLoader />;
  }

  const hasData = data.blocking.length > 0 || data.waiting.length > 0 || data.resolved.length > 0;

  // Pending = all currently on-hold tasks (blocking + waiting combined)
  const pendingTasks = [...(data.blocking || []), ...(data.waiting || [])];

  // Resolved items come entirely from the backend (includes released held tasks + completed dependency tasks)
  const allResolved = data.resolved || [];

  if (!hasData) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a202c", margin: 0 }}>Dependencies</h3>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Tasks on hold that are blocking progress</div>
        </div>
        <div className="flex justify-center items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="text-3xl text-gray-400">
              <CheckCircleOutlined />
            </div>
            <div className="text-lg pri-font text-gray-400">
              No dependencies
            </div>
            <div className="text-gray-400 text-sm">
              No tasks are currently on hold
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a202c", margin: 0 }}>Dependencies</h3>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Tasks on hold that are blocking progress</div>
        </div>
        {pendingTasks.length > 0 && (
          <Tag color="orange" style={{ fontSize: 12 }}>
            {pendingTasks.length} pending
          </Tag>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Pending — all on-hold tasks */}
        <div style={{ border: "1px solid #fde68a", borderRadius: 8, padding: 16, backgroundColor: "#fffbeb" }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#92400e", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <ClockCircleOutlined />
            Pending ({pendingTasks.length})
          </div>

          {pendingTasks.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: "20px 0" }}>
              No pending tasks
            </div>
          ) : (
            pendingTasks.map((task) => (
              <div key={task.id} style={{ backgroundColor: "#ffffff", border: "1px solid #fde68a", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar size={32} style={{ backgroundColor: "#f97316", fontSize: 12 }}>
                      {task.ownerName?.charAt(0)}{task.ownerLastName?.charAt(0)}
                    </Avatar>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{task.ownerName} {task.ownerLastName?.charAt(0)}.</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{task.ownerDesignation}</div>
                    </div>
                  </div>
                  <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>{task.daysOnHold}d</Tag>
                </div>
                <div style={{ fontSize: 12, color: "#334155", marginBottom: 4 }}>
                  <span style={{ color: "#64748b" }}>Task: </span>{task.name}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {task.holdReason}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Resolved */}
        <div style={{ border: "1px solid #86efac", borderRadius: 8, padding: 16, backgroundColor: "#f0fdf4" }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#166534", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCircleOutlined />
            Resolved ({allResolved.length})
          </div>

          {allResolved.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: "20px 0" }}>
              No tasks resolved this week
            </div>
          ) : (
            allResolved.map((task, idx) => (
              <div key={task.id || idx} style={{ backgroundColor: "#ffffff", border: "1px solid #86efac", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#166534", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                  ✓ {task.name}
                  {task.resolveType === "released" && (
                    <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, backgroundColor: "#dcfce7", color: "#166534", fontWeight: 700, border: "1px solid #86efac" }}>Released</span>
                  )}
                  {task.resolveType === "dependency" && (
                    <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, backgroundColor: "#dbeafe", color: "#1e40af", fontWeight: 700, border: "1px solid #93c5fd" }}>Resolved</span>
                  )}
                </div>
                {task.resolveType === "released" && task.releasedBy ? (
                  <div style={{ fontSize: 11, color: "#059669", marginBottom: 2, fontWeight: 600 }}>{task.releasedBy}</div>
                ) : task.resolveType === "dependency" && task.releasedHeldTask ? (
                  <div style={{ fontSize: 11, color: "#2563eb", marginBottom: 2, fontWeight: 600 }}>Helped release "{task.releasedHeldTask}"</div>
                ) : task.holdReason ? (
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>{task.holdReason}</div>
                ) : null}
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {task.resolveType === "released" ? "Released" : "Resolved"} {task.resolvedOn ? moment(task.resolvedOn).format("MMM DD") : task.taskEndDate ? moment(task.taskEndDate).format("MMM DD") : ""} {task.daysToResolve ? `· ${task.daysToResolve} days` : ""}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
